import { Button, Group, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { closeSupportSession, type SupportAccessSummary } from '@/shared/api/auth';
import { forgetSession } from '@/shared/session/session';
import { formatDate } from '@/shared/format';

/**
 * Persistent band of the console during a support session (13 §3.4, block C): visible on every
 * screen, never something a person can dismiss and forget about. "Salir" leaves the tenant but
 * never revokes the grant (ISO-25) - the platform administrator can come back while it lasts.
 */
export function SupportSessionBanner({ supportAccess }: { supportAccess: SupportAccessSummary }) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const exit = useMutation({
    mutationFn: () => closeSupportSession(),
    onSuccess: async () => {
      forgetSession(queryClient);
      await navigate({ to: '/platform/support-access' });
    },
  });

  return (
    <Group
      role="status"
      gap="sm"
      justify="center"
      py={6}
      wrap="wrap"
      style={{
        background: 'var(--mantine-color-orange-1)',
        borderBottom: '1px solid var(--mantine-color-orange-4)',
      }}
    >
      <Text size="sm" fw={600} c="orange.9">
        {t('supportSession.banner', {
          tenant: supportAccess.tenantName,
          expiresAt: formatDate(supportAccess.expiresAt),
        })}
      </Text>
      <Button
        size="compact-xs"
        variant="outline"
        color="orange"
        loading={exit.isPending}
        onClick={() => exit.mutate()}
      >
        {t('supportSession.exit')}
      </Button>
    </Group>
  );
}
